import { useMemo } from 'react';
import { Card } from '../../../molecules/Card/Card';
import {
  isEmptyTotal,
  type ChartRow,
  type ChartBaseProps,
} from '../../../molecules/ChartFrame/chartProps';
import type { ChartSeriesToken } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { useLegendToggle } from '../../../molecules/ChartLegend/useLegendToggle';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { DonutChart } from '../../../organisms/DonutChart/DonutChart';
import { token } from '../../../tokens/tokens';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type ShareOfVoiceSlice = {
  /** The brand's option value. */
  key: string;
  label: string;
  color: ChartSeriesToken;
};

export type ShareOfVoiceView = {
  /** `Your share` (badge in points) and `Lead over next`. */
  kpis: KPIMetric[];
  /** One slice per brand with a mention, in chip order — the donut's and the legend's. */
  slices: ShareOfVoiceSlice[];
  /** One row per slice: `{ [nameKey]: label, mentions }`. */
  rows: ChartRow[];
  nameKey: string;
  /** Our share, or `—` when our brand is not in the category. */
  centerValue: string;
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type ShareOfVoiceCardProps = WidgetBlockProps & {
  view?: ShareOfVoiceView;
  /**
   * What the denominator is, which is the one thing that differs between the
   * card's two callers — and a difference the help text has to carry, because
   * a tooltip explaining the `Brands` chip on a screen that does not draw one
   * is worse than no tooltip. `chip` on Competitors, `all` on Overview.
   */
  category: 'chip' | 'all';
  /** Opens the conversations behind a slice (index into `rows`). */
  onViewDetails?: (index: number) => void;
  /**
   * The executive dashboard draws the same donut for a share of engagement
   * (`Share of voice by engagement`), so it names the card, its help and the
   * figure in the middle itself. Default: Figma's, by `category`.
   */
  title?: string;
  helpText?: string;
  helpSupportingText?: string;
  centerLabel?: string;
  /**
   * Each legend entry shows or hides its brand (the executive view, GP
   * 2026-11-03). Only what is drawn changes: the figures above stay of every
   * brand. Default off.
   */
  toggleable?: boolean;
};

/**
 * Competitors' first card (931:36991), and Overview's (972:19386).
 *
 * **A cross-brand card** — the whole point is the field we stand in. On
 * Competitors the `Brands` chip is on screen, so share of voice is share of the
 * *selected* brands: deselect an operator and it leaves the denominator as well
 * as the plot, which keeps the slices summing to the figure in the middle. The
 * user can also deselect **us**, and every "our …" figure then reads an em dash
 * rather than a confident `0.0%`. On Overview no brand can be deselected, so
 * the em-dash branches simply never fire there.
 *
 * Two KPIs, not the three Figma draws: `Rank` was dropped on request
 * 2026-09-20 — a deliberate deviation; the donut already ranks the field by eye.
 */
export function ShareOfVoiceCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  category,
  onViewDetails,
  title = 'Share of voice',
  helpText,
  helpSupportingText,
  centerLabel = 'your share of voice',
  toggleable = false,
}: ShareOfVoiceCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmptyTotal(view.rows, 'mentions');
  const keys = useMemo(() => view?.slices.map((s) => s.key) ?? [], [view]);
  const legend = useLegendToggle(keys);
  /* The slices drawn, as indexes into the view's; every one unless the legend hid some. */
  const drawn = toggleable ? legend.shown : keys.map((_, i) => i);
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title={title}
      helpText={
        helpText ??
        (category === 'all'
          ? 'Shows how total mentions are divided among your brand and competitors. The comparison group includes every tracked brand on this page.'
          : 'Each selected brand’s share of total mentions across the selected brands. Changing the Brands filter changes the comparison group.')
      }
      helpSupportingText={helpSupportingText}
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <DonutChart
            data={drawn.map((i) => view.rows[i]!)}
            onViewDetails={onViewDetails && ((index) => onViewDetails(drawn[index]!))}
            nameKey={view.nameKey}
            valueKey="mentions"
            height={PLOT_HEIGHT}
            legend="none"
            centerLabel={centerLabel}
            centerValue={view.centerValue}
            colors={drawn.map((i) => view.slices[i]!.color)}
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={
        view && (
          <ChartLegend
            items={view.slices.map((s) => ({
              label: s.label,
              color: token[s.color],
              off: toggleable && legend.isOff(s.key),
            }))}
            onToggle={toggleable ? (i) => legend.toggle(keys[i]!) : undefined}
          />
        )
      }
    />
  );
}
